<script lang="ts">
	//Services
	import { format } from 'svelte-i18n';
	import { postReq, localReq } from '../../../../services/http';
	//Stores
		import { getDeviceStatus, getDeviceSettings } from 'src/stores/device-runtime/status-selectors';
	//Helpers
	import { get_local_ip, real_id } from '../../../../helpers/interface_helpers';
	import { classMap } from '@Shelly/shelly-design-system/helpers/component-utils';
	//Design system components
	import { Button, Checkbox, Toggle, Radio, Text, toast } from '@Shelly/shelly-design-system';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';
	import { getContext, onDestroy } from 'svelte';
	import type { Writable } from 'svelte/store';
	import type { SidepanelStickyBottomProps } from 'src/components/Sidepanel/sidepanel-types';

	export let device;

	$: settingsStore = getDeviceSettings(device.id);
	$: settings = $settingsStore;
	let enabled = settings.ext_switch_enable;
	let cacheEnabled = enabled;
	let flipping = settings.ext_switch[device.channel].relay_num;
	let cacheFlipping = flipping;
	let reverse = settings.ext_switch_reverse;
	let cacheReverse = reverse;
	let icon = device.ext_switch_icon;
	let cacheIcon = icon;
	const localIp = get_local_ip(device.id);
	let loading = false;

	const toggleEnabled = async () => {
		enabled = !enabled;
		if ($showNewLayout) {
			sentRequests();
		}
	};
	//TODO turning off request fails
	const flippingSwitch = async () => {
		flipping === 0 ? (flipping = -1) : (flipping = 0);
	};

	const toggleReverse = async () => {
		reverse = !reverse;
	};

	const setIconType = async (val) => {
		icon = val;
	};

	const sentRequests = async () => {
		let saveEnabled;
		let saveFlipping;
		let saveReverse;
		let saveIcon;
		if (cacheEnabled !== enabled) {
			loading = true;
			if (localIp) {
				saveEnabled = await localReq(
					device,
					`${localIp}/settings`,
					'post',
					{ ext_switch_enable: enabled },
					6000,
					true,
				);
			} else {
				saveEnabled = await postReq(`/device/generic_command`, {
					id: real_id(device.id),
					command: 'ext_switch_enable',
					payload: JSON.stringify({ enable: enabled }),
				});
			}
		}
		if (cacheFlipping !== flipping) {
			loading = true;
			if (localIp) {
				saveFlipping = await localReq(
					device,
					`${localIp}/settings/ext_switch/${device.channel}`,
					'GET',
					{ relay_num: flipping },
					6000,
					true,
				);
			} else {
				saveFlipping = await postReq(`/device/generic_command`, {
					id: real_id(device.id),
					command: 'ext_switch',
					payload: JSON.stringify({ id: device.channel, relay_num: flipping }),
				});
			}
		}
		if (cacheReverse !== reverse) {
			loading = true;
			if (localIp) {
				saveReverse = await localReq(
					device,
					`${localIp}/settings/ext_switch`,
					'post',
					{ ext_switch_reverse: reverse },
					6000,
					true,
				);
			} else {
				saveReverse = await postReq(`/device/generic_command`, {
					id: real_id(device.id),
					command: 'ext_switch_reverse',
					payload: JSON.stringify({ reverse: reverse }),
				});
			}
		}
		if (cacheIcon !== icon) {
			loading = true;
			saveIcon = await postReq(`/interface/device/bulk_update`, {
				devices_data: `{"${real_id(device.id)}":{"ext_switch_icon":"${icon}"}}`,
			});
		}
		Promise.all([saveEnabled, saveFlipping, saveReverse, saveIcon]).then((res) => {
			if (typeof res[0] !== 'undefined') {
				if (res[0].isok) {
					toast.open({
						type: 'success',
						title: enabled
							? $format('ext_switch_enabled_scc_toast')
							: $format('ext_switch_disabled_scc_toast', {
									default: `External switch add-on is ${enabled ? 'enabled' : 'disabled'}.`,
								}),
					});
					cacheEnabled = enabled;
				} else {
					toast.open({ type: 'danger', title: $format('error_global') });
					enabled = cacheEnabled;
				}
			}
			if (typeof res[1] !== 'undefined') {
				if (res[1].isok) {
					toast.open({
						type: 'success',
						title: flipping
							? $format('ext_switch_flipped_scc_toast')
							: $format('ext_switch_normal_scc_toast', {
									default: `External switch output ON/Off is ${
										flipping === 1 ? 'flipped' : 'normal'
									}.`,
								}),
					});
					cacheFlipping = flipping;
				} else {
					toast.open({ type: 'danger', title: $format('error_global') });
					flipping = cacheFlipping;
				}
			}
			if (typeof res[2] !== 'undefined') {
				if (res[2].isok) {
					toast.open({
						type: 'success',
						title: reverse
							? $format('ext_switch_position_reversed_scc_toast')
							: $format('ext_switch_position_normal_scc_toast', {
									default: `External switch position is ${reverse ? 'reversed' : 'not reversed'}.`,
								}),
					});
					cacheReverse = reverse;
				} else {
					toast.open({ type: 'danger', title: $format('error_global') });
					reverse = cacheReverse;
				}
			}
			if (typeof res[3] !== 'undefined') {
				if (res[3].isok) {
					toast.open({
						type: 'success',
						title: $format('ext_icon_scc_toast', {
							values: { value: $format(`icon_name_${icon}`) },
							default: `Successfully changed to ${icon}.`,
						}),
					});
					cacheIcon = icon;
				} else {
					icon = cacheIcon;
					toast.open({ type: 'danger', title: $format('error_global') });
				}
			}
			loading = false;
		});
	};

	const stickyBottomAction =
		getContext<Writable<SidepanelStickyBottomProps | null>>('stickyBottomAction');

	$: if (stickyBottomAction) {
		if ($showNewLayout && enabled) {
			stickyBottomAction.set({ onClick: sentRequests, loading, disabled: false });
		} else {
			stickyBottomAction.set(null);
		}
	}

	onDestroy(() => {
		stickyBottomAction?.set(null);
	});
</script>

{#if $showNewLayout}
	<Toggle size="m" class="mb-s-plus" checked={enabled} on:click={toggleEnabled} id="checkbox-kvezw1l">
		{$format('label_enable_external_switch', {
			default: 'Enable external switch',
		})}
	</Toggle>
	{#if enabled}
		<div
			class={classMap({
				'cursor-not-allowed': !enabled,
			})}
		>
			<Text
				class={classMap({
					'mb-s': true,
					'text-color-3': !enabled,
				})}
				styleType="body-small"
			>
				{$format('choose_one_or_more_options', {
					default: 'Choose one or more of the following options:',
				})}
			</Text>
			<Checkbox
				size="m"
				class="mb-s"
				checked={flipping === 0}
				disabled={!enabled}
				on:click={flippingSwitch}
				id="checkbox-1whoxuz"
				>{$format('ext_switch_flip_output', {
					default: 'Choose external switch add-on interface icon:',
				})}</Checkbox
			>
			<Checkbox
				size="m"
				class="mb-m"
				checked={reverse}
				disabled={!enabled}
				on:click={toggleReverse}
				id="checkbox-iqvs48l"
				>{$format('ext_switch_reverse_position', {
					default: 'Reverse switch position',
				})}</Checkbox
			>
			<Text
				class={classMap({
					'text-500 mb-s': true,
					'text-color-3': !enabled,
				})}
				styleType="body-small"
			>
				{$format('ext_switch_select_icon', {
					default: 'Choose external switch add-on interface icon:',
				})}
			</Text>
			<Radio
				size="m"
				class="mb-s"
				value="basic"
				disabled={!enabled}
				bind:selected={icon}
				unavailable={icon === 'basic'}
				on:click={() => setIconType('basic')}
				id="radio-v8n2zka"
			>
				{$format('icon_name_basic', { default: 'Basic' })}
			</Radio>
			<Radio
				size="m"
				class="mb-s"
				value="door"
				disabled={!enabled}
				bind:selected={icon}
				unavailable={icon === 'door'}
				on:click={() => setIconType('door')}
				id="radio-bxs2mt0"
			>
				{$format('icon_name_door', { default: 'Door' })}
			</Radio>
			<Radio
				size="m"
				value="garage"
				disabled={!enabled}
				bind:selected={icon}
				unavailable={icon === 'garage'}
				on:click={() => setIconType('garage')}
				id="radio-8venoja"
			>
				{$format('icon_name_garage', { default: 'Garage door' })}
			</Radio>
		</div>
	{/if}
{:else}
	<Checkbox size="l" class="mb-s" checked={enabled} on:click={toggleEnabled} id="checkbox-kvezw1l">
		{$format('label_enable_external_switch', {
			default: 'Enable external switch',
		})}
	</Checkbox>
	<hr class="mb-m" />

	<div
		class={classMap({
			'mb-m': true,
			'cursor-not-allowed': !enabled,
		})}
	>
		<Text
			class={classMap({
				'text-bold mb-s': true,
				'text-color-3': !enabled,
			})}
			styleType="body-small"
		>
			{$format('choose_one_or_more_options', {
				default: 'Choose one or more of the following options:',
			})}
		</Text>
		<Checkbox
			size="m"
			class="mb-s"
			checked={flipping === 0}
			disabled={!enabled}
			on:click={flippingSwitch}
			id="checkbox-1whoxuz"
			>{$format('ext_switch_flip_output', {
				default: 'Choose external switch add-on interface icon:',
			})}</Checkbox
		>
		<Checkbox
			size="m"
			class="mb-m"
			checked={reverse}
			disabled={!enabled}
			on:click={toggleReverse}
			id="checkbox-iqvs48l"
			>{$format('ext_switch_reverse_position', {
				default: 'Reverse switch position',
			})}</Checkbox
		>
		<Text
			class={classMap({
				'text-bold mb-s': true,
				'text-color-3': !enabled,
			})}
			styleType="body-small"
		>
			{$format('ext_switch_select_icon', {
				default: 'Choose external switch add-on interface icon:',
			})}
		</Text>
		<Radio
			size="m"
			class="mb-s"
			value="basic"
			disabled={!enabled}
			bind:selected={icon}
			unavailable={icon === 'basic'}
			on:click={() => setIconType('basic')}
			id="radio-v8n2zka"
		>
			{$format('icon_name_basic', { default: 'Basic' })}
		</Radio>
		<Radio
			size="m"
			class="mb-s"
			value="door"
			disabled={!enabled}
			bind:selected={icon}
			unavailable={icon === 'door'}
			on:click={() => setIconType('door')}
			id="radio-bxs2mt0"
		>
			{$format('icon_name_door', { default: 'Door' })}
		</Radio>
		<Radio
			size="m"
			value="garage"
			disabled={!enabled}
			bind:selected={icon}
			unavailable={icon === 'garage'}
			on:click={() => setIconType('garage')}
			id="radio-8venoja"
		>
			{$format('icon_name_garage', { default: 'Garage door' })}
		</Radio>
	</div>

	<Button {loading} size="m" width="full" on:click={sentRequests}>
		{$format('button_save')}
	</Button>
{/if}
